import type { ErrorResponse } from '../../../../shared/types/epics';
import type { ActionKey, TimelineItem } from '../../../../shared/lib/epicActionCenter';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

export interface EpicTimeline {
  state: string;
  issueUrl: string;
  issueNumber: number;
  items: TimelineItem[];
  /** Latest Forge prompt no person has answered yet. */
  pending: TimelineItem | null;
  /** Latest Forge failure nobody has acted on yet. */
  blocker: TimelineItem | null;
}

async function failure(r: Response, fallback: string): Promise<Error> {
  let message = `${fallback}: ${r.statusText}`;
  try {
    const b = (await r.json()) as ErrorResponse;
    if (b?.error?.message) message = b.error.message;
  } catch { /* not JSON */ }
  return new Error(message);
}

/** GitHub issue thread for an epic (epicRef), classified into timeline items. */
export async function fetchEpicTimeline(epicRef: string): Promise<EpicTimeline> {
  const r = await authFetch(`${API_BASE}/epics/${encodeURIComponent(epicRef)}/timeline`);
  if (!r.ok) throw await failure(r, 'Failed to load activity');
  return ((await r.json()) as { data: EpicTimeline }).data;
}

export type PostEpicCommentRequest =
  | { action: ActionKey; input?: string }
  | { text: string };

/** Post an Action Center button, reply or note as a GitHub comment. */
export async function postEpicComment(
  epicRef: string,
  req: PostEpicCommentRequest,
): Promise<{ body: string; commentUrl: string }> {
  const r = await authFetch(`${API_BASE}/epics/${encodeURIComponent(epicRef)}/comment`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!r.ok) throw await failure(r, 'Failed to post');
  return ((await r.json()) as { data: { body: string; commentUrl: string } }).data;
}
